import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import {
  PageBuilder,
  PageContent,
  TrackingEvent,
} from '@hey-car/intl-seo-hub.page-builder';
import { Responses, IContentItem } from '@kontent-ai/delivery-sdk';
import Head from 'next/head';
import {
  getArticles,
  getFeaturedBlogArticles,
  getItemByCodeName,
  getRecentBlogArticles,
} from 'app/apiCalls/kentico';
import '@hey-car/intl-seo-hub.page-builder/dist/index.css';
import { useTrackingPageContext } from 'app/shared/components/PageWrapper/providers/TrackingPageProvider/TrackingPageProvider';
import KenticoPage from 'app/containers/KenticoPage/KenticoPage';
import { ParsedUrlQuery } from 'querystring';

export interface IBlogHubPage {
  blogHubPageContent: PageContent;
  featuredArticles: IContentItem[];
  recentArticles: IContentItem[];
  allArticles: Responses.IListContentItemsResponse;
  currentPage: number;
  pageId?: string;
  kenticoProjectId: string;
  url: string;
}

interface IBlogHubPageProps extends IBlogHubPage {
  linkPrefix: string;
}

const BlogHubPage = ({
  currentPage,
  url,
  pageId,
  ...props
}: IBlogHubPageProps) => {
  const pageData = props.blogHubPageContent;
  const href = generateUrlForBlogPagePagination(url, currentPage + 1);
  return (
    <KenticoPage pageData={pageData} pageIdPrefix={pageId}>
      <Head>
        <link rel="prefetch" href={href} as="document" key="prefetch" />
      </Head>
      <BlogHubPageContent url={url} {...props} />
    </KenticoPage>
  );

  function generateUrlForBlogPagePagination(
    pageUrl: string,
    pageNumber: number,
  ) {
    const newUrl = new URL(pageUrl);
    newUrl.searchParams.set('p', pageNumber.toString());
    return newUrl.toString();
  }
};

interface IBlogHubPageContentProps
  extends Omit<IBlogHubPageProps, 'currentPage'> {}

const BlogHubPageContent = ({
  blogHubPageContent,
  url,
  kenticoProjectId,
  ...props
}: IBlogHubPageContentProps) => {
  const { trackAction } = useTrackingPageContext();

  const handleTracking = (event: TrackingEvent) =>
    trackAction(event.event_name, event.properties);

  const extras = { ...props, paginationUrl: url };

  return (
    <PageBuilder
      page={blogHubPageContent}
      extras={extras}
      handleTracking={handleTracking}
      projectId={kenticoProjectId}
    />
  );
};

type UrlProtocol = 'http' | 'https';
type ServerSideUrl = `${UrlProtocol}://${string}`;

interface IServerSideBlogHubPageProps extends ParsedUrlQuery {
  itemCodename: string;
  types: string | string[];
}

const getServerSideProps: GetServerSideProps<
  IBlogHubPage,
  IServerSideBlogHubPageProps
> = async (context: GetServerSidePropsContext) => {
  const url: string = generateUrlFromServerSide(context);
  const { p } = context.query;
  const currentPage = (p && +p) || 1;

  const blogHubPageContent = await getItemByCodeName(
    context.params.itemCodename as string,
  ).catch(() => null);
  const featuredArticles = await getFeaturedBlogArticles(
    context.params.types as string[],
  ).catch(() => []);
  const recentArticles = await getRecentBlogArticles(
    context.params.types as string[],
  ).catch(() => []);
  const allArticles = await getArticles(
    currentPage,
    3,
    context.params.types as string[],
  ) // we display 3 recent articles in desktop
    .catch(() => ({} as Responses.IListContentItemsResponse));

  if (!blogHubPageContent || (p && allArticles.items?.length < 1)) {
    return { notFound: true };
  }

  // This is to allow prefetching (next.js <= 12 sends no-cache)
  context.res.setHeader('Cache-Control', 'max-age=240, must-revalidate');

  return {
    props: {
      blogHubPageContent,
      featuredArticles,
      recentArticles,
      allArticles,
      currentPage,
      url,
      kenticoProjectId: process.env.KENTICO_OTP_PROJECT_ID,
    },
  };

  function generateUrlFromServerSide(
    context: GetServerSidePropsContext,
  ): ServerSideUrl {
    const urlProtocol =
      process.env.NODE_ENV !== 'production' ? 'http' : 'https';
    return `${urlProtocol}://${context.req.headers.host}${context.resolvedUrl}`;
  }
};

export { BlogHubPage, getServerSideProps };
